import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { QueryParamEditor } from "@/components/domain/query-param-editor"
import type { Endpoint, HttpMethod } from "@/types/endpoint"

type Props = {
  endpoint: Endpoint | null            // null when creating a new draft
  httpMethods: HttpMethod[]
  isDraft: boolean                      // true when this is a new in-memory record
  onSaved: () => void                   // called after successful save
  onCancel: () => void                  // discard a draft / unsaved edits
  onDelete: () => void                  // delete an existing endpoint
}

const BLANK = {
  name: "",
  key: "",
  httpMethod: "GET" as HttpMethod,
  pathTemplate: "/",
  timeoutMs: "5000",
  cacheTtlSeconds: "0",
  querySchemaJson: "[]",
}

// Auto-derive a slug from a free-text name. Lowercase, replace non-alphanum
// with underscore, collapse runs, trim leading/trailing underscores.
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
}

export function EndpointForm({ endpoint, httpMethods, isDraft, onSaved, onCancel, onDelete }: Props) {
  const [fields, setFields] = useState(BLANK)
  const [submitting, setSubmitting] = useState(false)
  // Track whether the user has hand-edited the key. While false, typing in
  // Name auto-fills the key. After the user touches Key directly, stop syncing.
  const [keyTouched, setKeyTouched] = useState(false)
  const [schemaInfoOpen, setSchemaInfoOpen] = useState(false)

  useEffect(() => {
    if (endpoint) {
      setFields({
        name: endpoint.name,
        key: endpoint.key,
        httpMethod: endpoint.httpMethod,
        pathTemplate: endpoint.pathTemplate,
        timeoutMs: endpoint.timeoutMs?.toString() ?? "5000",
        cacheTtlSeconds: endpoint.cacheTtlSeconds?.toString() ?? "0",
        querySchemaJson: JSON.stringify(endpoint.querySchema ?? [], null, 2),
      })
      setKeyTouched(true) // existing endpoints — don't auto-rewrite the key
    } else {
      setFields(BLANK)
      setKeyTouched(false)
    }
  }, [endpoint?.id, isDraft])

  const keyImmutable = !isDraft && !!endpoint?.keyImmutable

  const handleNameChange = (val: string) => {
    setFields((f) => ({ ...f, name: val, key: keyTouched ? f.key : slugify(val) }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const payload = {
      endpoint: {
        name: fields.name,
        key: fields.key,
        http_method: fields.httpMethod,
        path_template: fields.pathTemplate,
        timeout_ms: fields.timeoutMs ? parseInt(fields.timeoutMs, 10) : null,
        cache_ttl_seconds: fields.cacheTtlSeconds ? parseInt(fields.cacheTtlSeconds, 10) : 0,
        query_schema_json: fields.querySchemaJson,
      }
    }
    const onFinish = () => { setSubmitting(false); onSaved() }
    if (isDraft || !endpoint) {
      router.post("/endpoints", payload, { preserveScroll: true, onFinish })
    } else {
      router.patch(`/endpoints/${endpoint.id}`, payload, { preserveScroll: true, onFinish })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col rounded-card border border-border bg-app-panel">
      <div className="border-b border-border p-4">
        <div className="text-sm font-medium text-app-text">Endpoint Details</div>
        <div className="text-xs text-app-muted">Edit the selected endpoint configuration.</div>
      </div>

      <div className="space-y-4 overflow-y-auto p-4">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-app-muted">Name</label>
          <input type="text" required value={fields.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Coin balance lookup"
            className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none" />
          <p className="mt-1 text-xs text-app-muted">Human label shown in lists and breadcrumbs. Free text.</p>
        </div>

        <div>
          <label className="mb-1 flex items-center justify-between text-xs uppercase tracking-wide text-app-muted">
            <span>Key (slug)</span>
            {keyImmutable && (
              <span className="normal-case text-[11px] text-vip-goldSoft">
                🔒 referenced by {endpoint?.referenceCount ?? 0} skill{(endpoint?.referenceCount ?? 0) === 1 ? "" : "s"}
              </span>
            )}
          </label>
          <input type="text" required value={fields.key}
            onChange={(e) => { setKeyTouched(true); setFields((f) => ({ ...f, key: e.target.value })) }}
            disabled={keyImmutable}
            pattern="[a-z0-9_]+"
            placeholder="coin_balance"
            className="w-full rounded-control border border-border bg-app-bg px-3 py-2 font-mono text-sm text-app-text focus:border-brand-red focus:outline-none disabled:cursor-not-allowed disabled:bg-app-panel disabled:text-app-muted" />
          <p className="mt-1 text-xs text-app-muted">
            {keyImmutable
              ? "Slugs are locked once a skill references them. Detach all references to rename, or delete and recreate."
              : "Lowercase letters, digits, underscores. Used by skills (e.g. fetch_partner_data(endpoint_name: \"coin_balance\"))."}
          </p>
        </div>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-app-muted">HTTP Method</label>
          <select value={fields.httpMethod}
            onChange={(e) => setFields((f) => ({ ...f, httpMethod: e.target.value as HttpMethod }))}
            className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none">
            {httpMethods.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-app-muted">Path Template</label>
          <input type="text" required value={fields.pathTemplate}
            onChange={(e) => setFields((f) => ({ ...f, pathTemplate: e.target.value }))}
            placeholder="/api/v1/users/{source_key}/coins"
            className="w-full rounded-control border border-border bg-app-bg px-3 py-2 font-mono text-sm text-app-text focus:border-brand-red focus:outline-none" />
          <p className="mt-1 text-xs text-app-muted">
            Use <span className="font-mono text-app-text">{"{source_key}"}</span> to reference your user's ID as a replacement token.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-app-muted">Timeout (ms)</label>
            <input type="number" min="100" value={fields.timeoutMs}
              onChange={(e) => setFields((f) => ({ ...f, timeoutMs: e.target.value }))}
              className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-app-muted">Cache TTL (seconds)</label>
            <input type="number" min="0" value={fields.cacheTtlSeconds}
              onChange={(e) => setFields((f) => ({ ...f, cacheTtlSeconds: e.target.value }))}
              className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none" />
          </div>
        </div>

        <div>
          <QueryParamEditor
            value={fields.querySchemaJson}
            onChange={(v) => setFields((f) => ({ ...f, querySchemaJson: v }))}
          />
          <div className="mt-2 text-right">
            <button
              type="button"
              onClick={() => setSchemaInfoOpen(true)}
              className="text-xs text-app-muted underline decoration-dotted hover:text-app-text"
            >
              Learn more about query schema
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border p-4">
        <div>
          {!isDraft && endpoint && (
            <button type="button" onClick={onDelete}
              disabled={endpoint.referenceCount > 0}
              title={endpoint.referenceCount > 0 ? `Referenced by ${endpoint.referenceCount} skill(s) — detach to enable delete` : undefined}
              className="rounded-control border border-status-error/40 bg-transparent px-3 py-2 text-xs font-medium text-status-error transition hover:bg-status-error/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent">
              Delete
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-control border border-border px-4 py-2 text-sm text-app-text transition hover:bg-white/5">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            className="rounded-control border border-brand-red bg-brand-red px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-redDark disabled:opacity-60">
            {submitting ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <Dialog open={schemaInfoOpen} onClose={() => setSchemaInfoOpen(false)} size="lg">
        <DialogHeader
          title="Query Schema"
          subtitle="A whitelist (and required-list) for query-string params on this endpoint."
        />
        <DialogBody>
          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Why it exists</h3>
            <p className="text-sm text-app-muted">
              The AI agent decides which params to send when it calls{" "}
              <span className="font-mono text-app-text">fetch_partner_data(extra_query: …)</span>.
              The schema is your contract: only listed params reach your API,
              and missing required params fail fast instead of hitting your
              backend with bad input. Path-template tokens like{" "}
              <span className="font-mono text-app-text">{"{source_key}"}</span>{" "}
              go in the path itself — query schema is for{" "}
              <span className="font-mono text-app-text">?key=value</span> bits.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Format</h3>
            <p className="mb-2 text-sm text-app-muted">JSON array. Each entry is either:</p>
            <ul className="ml-4 list-disc space-y-1 text-sm text-app-muted">
              <li>A string — shorthand for an optional param: <span className="font-mono text-app-text">"limit"</span></li>
              <li>
                An object — supports a JSON-Schema-style subset (each key is optional except <span className="font-mono text-app-text">name</span>):
                <ul className="ml-5 mt-1 list-[circle] space-y-0.5">
                  <li><span className="font-mono text-app-text">name</span> — snake_case param name</li>
                  <li><span className="font-mono text-app-text">type</span> — <span className="font-mono">string</span> | <span className="font-mono">integer</span> | <span className="font-mono">number</span> | <span className="font-mono">boolean</span></li>
                  <li><span className="font-mono text-app-text">format</span> (string only) — <span className="font-mono">date-time</span> | <span className="font-mono">date</span> | <span className="font-mono">uuid</span> | <span className="font-mono">email</span> | <span className="font-mono">uri</span></li>
                  <li><span className="font-mono text-app-text">enum</span> — array of allowed values (must match <span className="font-mono">type</span>)</li>
                  <li><span className="font-mono text-app-text">pattern</span> (string only) — regex string the value must match</li>
                  <li><span className="font-mono text-app-text">minimum</span> / <span className="font-mono text-app-text">maximum</span> (numeric only)</li>
                  <li><span className="font-mono text-app-text">required</span> — true to fail-fast when missing</li>
                  <li><span className="font-mono text-app-text">default</span> — shown to the AI as a hint (NOT auto-injected)</li>
                  <li><span className="font-mono text-app-text">description</span> — human / AI-facing one-liner</li>
                </ul>
              </li>
            </ul>
            <p className="mt-2 text-xs text-app-muted">
              Strict subset — anything outside this list (e.g. <span className="font-mono">oneOf</span>, <span className="font-mono">$ref</span>) is rejected on save. See the full JSON Schema reference at{" "}
              <a href="https://json-schema.org/understanding-json-schema/reference" target="_blank" rel="noreferrer" className="underline decoration-dotted hover:text-app-text">json-schema.org</a> for what we may add later.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-app-text">Examples</h3>

            <div className="space-y-3">
              <div className="rounded-control border border-border bg-app-bg p-3">
                <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">No params (most lookups)</div>
                <pre className="overflow-x-auto font-mono text-xs text-app-text">[]</pre>
              </div>

              <div className="rounded-control border border-border bg-app-bg p-3">
                <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">Required ISO8601 date-time</div>
                <pre className="overflow-x-auto font-mono text-xs text-app-text">{`[
  { "name": "since",
    "type": "string",
    "format": "date-time",
    "required": true,
    "description": "Range start (ISO8601 UTC)." }
]`}</pre>
                <p className="mt-2 text-xs text-app-muted">
                  AI must supply a valid date-time or the call fails before reaching you with a structured error
                  the LLM can self-correct on.
                </p>
              </div>

              <div className="rounded-control border border-border bg-app-bg p-3">
                <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">Enum + range</div>
                <pre className="overflow-x-auto font-mono text-xs text-app-text">{`[
  { "name": "status", "type": "string", "enum": ["pending", "active", "closed"] },
  { "name": "limit",  "type": "integer", "minimum": 1, "maximum": 100, "default": 25 }
]`}</pre>
                <p className="mt-2 text-xs text-app-muted">
                  <span className="font-mono">status</span> rejects anything outside the three values.{" "}
                  <span className="font-mono">limit</span> coerces a string like <span className="font-mono">"25"</span> to the integer{" "}
                  <span className="font-mono">25</span> before sending.
                </p>
              </div>

              <div className="rounded-control border border-border bg-app-bg p-3">
                <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">Mix of legacy + rich</div>
                <pre className="overflow-x-auto font-mono text-xs text-app-text">{`[
  "event_name",
  { "name": "from", "type": "string", "format": "date-time" },
  { "name": "to",   "type": "string", "format": "date-time" }
]`}</pre>
                <p className="mt-2 text-xs text-app-muted">
                  Plain string entries still work — useful when you don't need any constraints beyond "this name is allowed."
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Behavior at request time</h3>
            <ul className="ml-4 list-disc space-y-1 text-sm text-app-muted">
              <li>Listed params are passed through (with type coercion when <span className="font-mono">type</span> is set).</li>
              <li>Unlisted params are silently dropped.</li>
              <li>A missing <span className="font-mono">required</span> param raises{" "}
                <span className="font-mono text-app-text">MissingQueryParam</span> before any HTTP request.
              </li>
              <li>A value that violates the schema raises{" "}
                <span className="font-mono text-app-text">InvalidQueryParam</span> with a descriptive
                message — the AI sees it as a structured tool error and self-corrects.
              </li>
              <li>The schema is also surfaced to the AI in the system prompt so it tries to send valid values on the FIRST call.</li>
            </ul>
          </section>
        </DialogBody>
        <DialogFooter>
          <button type="button" onClick={() => setSchemaInfoOpen(false)}
            className="rounded-control border border-border px-4 py-2 text-sm text-app-text transition hover:bg-white/5">
            Got it
          </button>
        </DialogFooter>
      </Dialog>
    </form>
  )
}
